import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { can } from "@family/shared";
import type { Album, AlbumsListResponse, CreateAlbumRequest, CreatePostRequest } from "@family/shared";
import { AppHeader } from "../components/AppHeader";
import { CameraCapture } from "../components/scan/CameraCapture";
import { PersonTagger, type PendingTag } from "../components/scan/PersonTagger";
import { ScanCaptureSteps } from "../components/scan/ScanCaptureSteps";
import { VoiceRecorder, type Recording } from "../components/scan/VoiceRecorder";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { useMe } from "../hooks/useMe";
import { apiFetch } from "../lib/api";
import { useScanCapture } from "../lib/scan/useScanCapture";
import { uploadPhoto } from "../lib/upload";
import { uploadVoiceNote } from "../lib/voiceNotes";

type Step = "capture" | "story" | "done";

const LAST_ALBUM_KEY = "scan:lastAlbumId";
const SHARE_KEY = "scan:shareToFeed";
const NEW_ALBUM = "__new__";
const TAG_BOX = 0.14;

function readLastAlbum(): string | null {
  try {
    return localStorage.getItem(LAST_ALBUM_KEY);
  } catch {
    return null;
  }
}

function readShare(): boolean {
  try {
    return localStorage.getItem(SHARE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Scan an old print: camera → corners → straighten and enhance → who, when
 * and the spoken story → saved into an album, and optionally shared as a
 * post in the family feed. See docs/ideas/scan-flow.md.
 */
export function ScanPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const scan = useScanCapture();
  const { result, useEnhanced, working, setWorking, error, setError, shownUrl } = scan;
  const [step, setStep] = useState<Step>("capture");

  const [tags, setTags] = useState<PendingTag[]>([]);
  const [year, setYear] = useState("");
  const [caption, setCaption] = useState("");
  const [voice, setVoice] = useState<Recording | null>(null);
  const [albumId, setAlbumId] = useState<string>(params.get("album") ?? readLastAlbum() ?? "");
  const [newAlbumTitle, setNewAlbumTitle] = useState("");
  const [shareToFeed, setShareToFeed] = useState(readShare);
  const [savedAlbum, setSavedAlbum] = useState<{ id: string; title: string } | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  const [shared, setShared] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // Each step is a fresh screen; don't land halfway down the next one.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, scan.step]);

  const albumsQuery = useQuery<AlbumsListResponse>({
    queryKey: ["albums", slug],
    queryFn: () => apiFetch<AlbumsListResponse>(`/families/${slug}/albums`),
    enabled: Boolean(slug),
  });
  const albums = useMemo(() => albumsQuery.data?.albums ?? [], [albumsQuery.data]);

  // Fall back to the first album (or "new") once the list is known.
  useEffect(() => {
    if (!albumsQuery.data) return;
    if (albumId && (albumId === NEW_ALBUM || albums.some((a) => a.id === albumId))) return;
    setAlbumId(albums[0]?.id ?? NEW_ALBUM);
  }, [albumsQuery.data, albums, albumId]);

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page"><p className="text-muted">Loading…</p></main>
      </>
    );
  }

  if (!can(primary.role, "media:upload") || !can(primary.role, "post:create")) {
    return (
      <>
        <AppHeader />
        <main className="page stack">
          <h1>Scan old photos</h1>
          <p className="text-muted">Your role in this family can view photos but not add them. Ask an admin if you'd like to help scan.</p>
        </main>
      </>
    );
  }

  const yearNum = year.trim() ? Number(year) : null;
  const thisYear = new Date().getFullYear();
  const yearInvalid = yearNum !== null && (!Number.isInteger(yearNum) || yearNum < 1800 || yearNum > thisYear);
  const needsTitle = albumId === NEW_ALBUM && !newAlbumTitle.trim();

  async function save() {
    if (!result || !slug || yearInvalid || needsTitle) return;
    setError(null);
    try {
      let target: { id: string; title: string };
      if (albumId === NEW_ALBUM) {
        setWorking("Creating the album…");
        const body: CreateAlbumRequest = { title: newAlbumTitle.trim() };
        const created = await apiFetch<Album>(`/families/${slug}/albums`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        target = { id: created.id, title: created.title };
      } else {
        const a = albums.find((x) => x.id === albumId);
        target = { id: albumId, title: a?.title ?? "the album" };
      }

      const story = {
        source: "scan" as const,
        ...(caption.trim() ? { caption: caption.trim() } : {}),
        ...(yearNum ? { takenAt: `${yearNum}-01-01T00:00:00.000Z`, takenAtPrecision: "year" as const } : {}),
      };
      const name = `scan-${Date.now()}.jpg`;

      // Keep the unenhanced crop too, so the enhancement can be undone later.
      let mediaId: string;
      if (useEnhanced) {
        setWorking("Uploading the original…");
        const originalId = await uploadPhoto(slug, new File([result.original], name, { type: "image/jpeg" }), {
          finalize: { source: "scan" },
        });
        setWorking("Uploading the enhanced photo…");
        mediaId = await uploadPhoto(slug, new File([result.enhanced], name, { type: "image/jpeg" }), {
          finalize: { ...story, originalMediaId: originalId },
        });
      } else {
        setWorking("Uploading the photo…");
        mediaId = await uploadPhoto(slug, new File([result.original], name, { type: "image/jpeg" }), {
          finalize: story,
        });
      }

      await apiFetch(`/families/${slug}/albums/${target.id}/media`, {
        method: "POST",
        body: JSON.stringify({ mediaIds: [mediaId] }),
      });

      if (tags.length > 0) {
        setWorking("Saving who's in it…");
        for (const t of tags) {
          const x = Math.min(Math.max(t.at.x - TAG_BOX / 2, 0), 1 - TAG_BOX);
          const y = Math.min(Math.max(t.at.y - TAG_BOX / 2, 0), 1 - TAG_BOX);
          await apiFetch(`/families/${slug}/media/${mediaId}/tags`, {
            method: "POST",
            body: JSON.stringify({ personId: t.personId, bbox: { x, y, w: TAG_BOX, h: TAG_BOX } }),
          });
        }
      }

      if (voice) {
        setWorking("Uploading the story…");
        await uploadVoiceNote(slug, mediaId, voice.blob, voice.durationMs);
      }

      // The photo is safe in the album by now; a failed post shouldn't undo
      // that, so report it on the done screen instead.
      setShared(false);
      setShareError(null);
      if (shareToFeed) {
        setWorking("Sharing in the feed…");
        const post: CreatePostRequest = {
          body: caption.trim() || `An old photo${yearNum ? ` from ${yearNum}` : ""}, scanned into ${target.title}.`,
          mediaIds: [mediaId],
          visibility: "everyone",
          ...(yearNum ? { eventDate: String(yearNum) } : {}),
        };
        try {
          await apiFetch(`/families/${slug}/posts`, { method: "POST", body: JSON.stringify(post) });
          setShared(true);
          void queryClient.invalidateQueries({ queryKey: ["posts", slug] });
        } catch (err) {
          setShareError(err instanceof Error ? err.message : String(err));
        }
      }

      try {
        localStorage.setItem(LAST_ALBUM_KEY, target.id);
      } catch {
        // Only a convenience.
      }
      setAlbumId(target.id);
      setNewAlbumTitle("");
      setSavedAlbum(target);
      setSavedCount((n) => n + 1);
      void queryClient.invalidateQueries({ queryKey: ["albums", slug] });
      void queryClient.invalidateQueries({ queryKey: ["album", slug, target.id] });
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWorking(null);
    }
  }

  function scanNext() {
    scan.reset();
    setTags([]);
    setYear("");
    setCaption("");
    setVoice(null);
    setStep("capture");
  }

  function onShareChange(next: boolean) {
    setShareToFeed(next);
    try {
      localStorage.setItem(SHARE_KEY, next ? "1" : "0");
    } catch {
      // Only a convenience.
    }
  }

  if (step === "capture" && scan.step === "camera") {
    return <CameraCapture onCapture={(c) => void scan.onCapture(c)} onClose={() => navigate(-1)} />;
  }

  return (
    <>
      <AppHeader />
      <main className="page stack-lg scan-page">
        <h1 style={{ margin: 0 }}>Scan old photos</h1>

        {step === "capture" && (
          <ScanCaptureSteps scan={scan} acceptLabel="Next" onAccept={() => setStep("story")} onRotated={() => setTags([])} />
        )}

        {step === "story" && shownUrl && (
          <section className="stack-lg">
            <div className="stack">
              <h2 className="scan-h2">Who is in it?</h2>
              <PersonTagger familySlug={slug} src={shownUrl} tags={tags} onChange={setTags} />
            </div>

            <label className="stack scan-field">
              <span className="scan-h2">Roughly what year?</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="e.g. 1974"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                style={{ maxWidth: 160 }}
              />
              {yearInvalid && <span className="text-error">Enter a year between 1800 and {thisYear}.</span>}
            </label>

            <div className="stack">
              <span className="scan-h2">Tell the story</span>
              <p className="text-subtle" style={{ margin: 0 }}>
                Where was it, what was happening? Speaking is often easier than typing. Up to 2 minutes.
              </p>
              <VoiceRecorder value={voice} onChange={setVoice} />
              <textarea
                rows={3}
                placeholder="Or write a caption"
                value={caption}
                maxLength={2000}
                onChange={(e) => setCaption(e.target.value)}
              />
            </div>

            <label className="stack scan-field">
              <span className="scan-h2">Save to album</span>
              <select className="select-skapa" value={albumId} onChange={(e) => setAlbumId(e.target.value)}>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}</option>
                ))}
                <option value={NEW_ALBUM}>+ New album…</option>
              </select>
              {albumId === NEW_ALBUM && (
                <input
                  type="text"
                  placeholder="Album name, e.g. Grandma's shoebox"
                  value={newAlbumTitle}
                  onChange={(e) => setNewAlbumTitle(e.target.value)}
                />
              )}
            </label>

            <label className="scan-check">
              <input type="checkbox" checked={shareToFeed} onChange={(e) => onShareChange(e.target.checked)} />
              <span>
                Also share it in the family feed
                <span className="text-subtle"> — everyone sees it on the home page, with the caption and year.</span>
              </span>
            </label>

            <div className="scan-actions">
              <button type="button" className="btn-tertiary" onClick={() => setStep("capture")} disabled={Boolean(working)}>
                ← Back
              </button>
              <button type="button" onClick={() => void save()} disabled={Boolean(working) || yearInvalid || needsTitle}>
                {working ?? "Save"}
              </button>
            </div>
          </section>
        )}

        {step === "done" && savedAlbum && (
          <section className="stack" style={{ alignItems: "flex-start" }}>
            {shownUrl && <img className="scan-preview scan-preview--small" src={shownUrl} alt="" />}
            <p style={{ margin: 0 }}>
              Saved to <strong>{savedAlbum.title}</strong>.
              {shared && " Shared in the family feed."}
              {savedCount > 1 && ` ${savedCount} photos scanned this session.`}
            </p>
            {shareError && <p className="text-error" style={{ margin: 0 }}>Couldn't share it in the feed: {shareError}</p>}
            <div className="scan-actions">
              <Link to={`/albums/${savedAlbum.id}`} className="btn-tertiary">Open the album</Link>
              <button type="button" onClick={scanNext}>Scan the next one</button>
            </div>
          </section>
        )}

        {error && <p className="text-error">{error}</p>}
      </main>
    </>
  );
}
